const LEVELS = {
  OFF: 0,
  INFO: 1,
  DEBUG: 2,
};

const normalizeLevel = (value) => {
  const normalizedValue = (value || "").toString().toUpperCase();
  return LEVELS.hasOwnProperty(normalizedValue) ? LEVELS[normalizedValue] : LEVELS.INFO;
};

let currentLevel = normalizeLevel(import.meta.env.VITE_LOG_LEVEL || "INFO");
let logMeta = {};

export const setLogLevel = (level = "INFO") => {
  currentLevel = normalizeLevel(level);
  console.info(`[client-logger] log level set to ${level.toUpperCase()}`);
  return currentLevel;
};

export const getLogLevel = () => currentLevel;

export const setLogMeta = (meta = {}) => {
  logMeta = { ...logMeta, ...meta };
  return logMeta;
};

export const getLogMeta = () => logMeta;

const shouldLog = (level) => {
  // In production, only log WARN and ERROR to reduce noise
  if (!import.meta.env.DEV) {
    // WARN and ERROR don't have level checks - they always log
    // INFO and DEBUG should be skipped in production
    if (level === LEVELS.INFO || level === LEVELS.DEBUG) {
      return false;
    }
  }
  return currentLevel >= level;
};

const formatErrorPart = (part) => {
  if (part instanceof Error) {
    return `${part.message}\n${part.stack || ""}`.trim();
  }
  if (part && typeof part === "object" && typeof part.stack === "string") {
    return `${part.message || "Error"}\n${part.stack}`.trim();
  }
  if (typeof part === "string") return part;
  try {
    return JSON.stringify(part, Object.getOwnPropertyNames(part));
  } catch {
    return String(part);
  }
};

const isPlainObject = (value) => {
  if (!value || typeof value !== "object") return false;
  if (value instanceof Error) return false;
  return value.constructor === Object;
};

const splitMeta = (parts) => {
  if (!parts.length) return { messageParts: [], meta: null };
  const last = parts[parts.length - 1];
  if (isPlainObject(last)) {
    return { messageParts: parts.slice(0, -1), meta: last };
  }
  return { messageParts: parts, meta: null };
};

const mergeMeta = (meta) => {
  if (!logMeta || Object.keys(logMeta).length === 0) return meta || null;
  if (!meta) return { ...logMeta };
  if (!isPlainObject(meta)) return meta;
  return { ...logMeta, ...meta };
};

const resolveLogEndpoint = () => {
  if (typeof window === "undefined") return null;
  const baseUrl = import.meta.env.VITE_SERVER_URL || window.location.origin;
  return `${String(baseUrl).replace(/\/$/, "")}/logs/client`;
};

// Batched log sender for production
class LogBatcher {
  constructor() {
    this.queue = [];
    this.batchSize = 10;
    this.flushInterval = 5000; // 5 seconds
    this.timer = null;

    // Flush on page unload
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => this.flush());
      window.addEventListener("pagehide", () => this.flush());
    }
  }

  add(payload) {
    this.queue.push({ ...payload, timestamp: Date.now() });

    if (this.queue.length >= this.batchSize) {
      this.flush();
    } else if (!this.timer) {
      this.timer = setTimeout(() => this.flush(), this.flushInterval);
    }
  }

  flush() {
    if (this.queue.length === 0) return;

    const logs = [...this.queue];
    this.queue = [];

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    // Send batched logs
    const endpoint = resolveLogEndpoint();
    if (!endpoint) return;

    try {
      const body = JSON.stringify({ logs });

      // Use sendBeacon for reliability (especially on page unload)
      if (navigator?.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(endpoint + "/batch", blob);
        return;
      }

      // Fallback to fetch
      fetch(endpoint + "/batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Ignore log transport errors
    }
  }
}

// Global batcher instance
const logBatcher = new LogBatcher();

const sendLogToServer = (payload) => {
  if (typeof window === "undefined") return;

  const isDev = import.meta.env.DEV;

  // In development, send immediately for debugging
  if (isDev) {
    const endpoint = resolveLogEndpoint();
    if (!endpoint) return;
    try {
      const body = JSON.stringify(payload);
      if (navigator?.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(endpoint, blob);
        return;
      }
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Ignore log transport errors
    }
    return;
  }

  // In production:
  // - Send WARN and ERROR immediately (critical)
  // - Batch INFO and DEBUG (non-critical)
  const isCritical = payload.level === "WARN" || payload.level === "ERROR";

  if (isCritical) {
    // Send critical logs immediately
    const endpoint = resolveLogEndpoint();
    if (!endpoint) return;
    try {
      const body = JSON.stringify(payload);
      if (navigator?.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(endpoint, blob);
        return;
      }
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Ignore log transport errors
    }
  } else {
    // Batch non-critical logs
    logBatcher.add(payload);
  }
};

const formatOutput = (level, context, parts) => {
  const timestamp = new Date().toISOString();
  const message = parts.map(formatErrorPart).join(" ");
  return `[${timestamp}] [${level}] [${context}] ${message}`;
};

class Logger {
  constructor(context) {
    this.context = context;
  }

  info(...parts) {
    if (shouldLog(LEVELS.INFO)) {
      const { messageParts, meta } = splitMeta(parts);
      const mergedMeta = mergeMeta(meta);
      const formatted = formatOutput("INFO", this.context, messageParts);
      if (mergedMeta) {
        console.info(formatted, mergedMeta);
      } else {
        console.info(formatted);
      }
      sendLogToServer({
        level: "INFO",
        context: this.context,
        message: messageParts.map(formatErrorPart).join(" "),
        formatted,
        meta: mergedMeta,
      });
    }
  }

  debug(...parts) {
    if (shouldLog(LEVELS.DEBUG)) {
      const { messageParts, meta } = splitMeta(parts);
      const mergedMeta = mergeMeta(meta);
      const formatted = formatOutput("DEBUG", this.context, messageParts);
      if (mergedMeta) {
        console.debug(formatted, mergedMeta);
      } else {
        console.debug(formatted);
      }
      sendLogToServer({
        level: "DEBUG",
        context: this.context,
        message: messageParts.map(formatErrorPart).join(" "),
        formatted,
        meta: mergedMeta,
      });
    }
  }

  warn(...parts) {
    const { messageParts, meta } = splitMeta(parts);
    const mergedMeta = mergeMeta(meta);
    const formatted = formatOutput("WARN", this.context, messageParts);
    if (mergedMeta) {
      console.warn(formatted, mergedMeta);
    } else {
      console.warn(formatted);
    }
    sendLogToServer({
      level: "WARN",
      context: this.context,
      message: messageParts.map(formatErrorPart).join(" "),
      formatted,
      meta: mergedMeta,
    });
  }

  error(...parts) {
    const { messageParts, meta } = splitMeta(parts);
    const mergedMeta = mergeMeta(meta);
    const formatted = formatOutput("ERROR", this.context, messageParts);
    if (mergedMeta) {
      console.error(formatted, mergedMeta);
    } else {
      console.error(formatted);
    }
    sendLogToServer({
      level: "ERROR",
      context: this.context,
      message: messageParts.map(formatErrorPart).join(" "),
      formatted,
      meta: mergedMeta,
    });
  }
}

export const getLogger = (context) => new Logger(context);
